import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { PasswordField } from '@/components/ui/TextField';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useResetPassword } from '@/auth/hooks';
import { errorMessage } from '@/lib/errorMessage';
import { ROUTES, MIN_PASSWORD_LENGTH } from '@/lib/config';
import { useT } from '@/i18n/useT';
import { rules } from './rules';

interface Form {
  password: string;
  confirm: string;
}

export function ResetPage() {
  const { t } = useT();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState('');
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<Form>();

  const onSubmit = handleSubmit(async (data) => {
    setServerError('');
    try {
      await reset.mutateAsync({ token, password: data.password });
      setDone(true);
    } catch (e) {
      setServerError(errorMessage(e));
    }
  });

  if (!token) {
    return (
      <Card title={t('reset.title')}>
        <Alert kind="error">{t('reset.missingToken')}</Alert>
        <Link className="link" to={ROUTES.forgot}>
          {t('reset.requestNew')}
        </Link>
      </Card>
    );
  }

  return (
    <Card title={t('reset.title')} subtitle={done ? undefined : t('reset.subtitle')}>
      {done ? (
        <>
          <Alert kind="success">{t('reset.success')}</Alert>
          <Link className="link" to={ROUTES.login}>
            {t('reset.goToLogin')}
          </Link>
        </>
      ) : (
        <>
          <Alert kind="error">{serverError}</Alert>
          <form onSubmit={onSubmit} noValidate>
            <PasswordField
              label={t('reset.newPassword')}
              autoComplete="new-password"
              autoFocus
              hint={t('signup.passwordHint', { min: MIN_PASSWORD_LENGTH })}
              error={errors.password?.message}
              disabled={reset.isPending}
              {...register('password', rules.newPassword)}
            />
            <PasswordField
              label={t('reset.confirmPassword')}
              autoComplete="new-password"
              error={errors.confirm?.message}
              disabled={reset.isPending}
              {...register('confirm', {
                validate: (v) => v === getValues('password') || t('reset.mismatch'),
              })}
            />
            <Button type="submit" busy={reset.isPending}>
              {reset.isPending ? t('reset.working') : t('reset.cta')}
            </Button>
          </form>
        </>
      )}
    </Card>
  );
}
